// FE-SCRM-055 — per-user Sales-CRM preferences.
//
// v1 stores these in localStorage because there is no UserPref BE
// table yet. Wrap reads/writes through a single module so the
// upgrade to a server-backed store is a one-file change. Exposes a
// React hook with a stable callback identity so settings forms can
// pass it as an onChange without re-rendering loops.

import { useCallback, useEffect, useState } from "react";

import { DEFAULT_USER_PREFS, type SalesCrmUserPreferences } from "./types";


export const USER_PREFS_STORAGE_KEY = "salesCrmUserPrefs";


export function readUserPrefs(): SalesCrmUserPreferences {
  try {
    const raw = window.localStorage.getItem(USER_PREFS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_USER_PREFS };
    const parsed = JSON.parse(raw) as Partial<SalesCrmUserPreferences>;
    return { ...DEFAULT_USER_PREFS, ...parsed };
  } catch {
    return { ...DEFAULT_USER_PREFS };
  }
}


export function writeUserPrefs(prefs: SalesCrmUserPreferences): void {
  try {
    window.localStorage.setItem(
      USER_PREFS_STORAGE_KEY, JSON.stringify(prefs),
    );
  } catch {
    // localStorage may be unavailable in a private-mode tab.
    // Silently noop — the in-memory copy still works for the session.
  }
}


export function useSalesCrmUserPrefs(): [
  SalesCrmUserPreferences,
  (next: Partial<SalesCrmUserPreferences>) => void,
] {
  const [prefs, setPrefs] = useState<SalesCrmUserPreferences>(
    () => readUserPrefs(),
  );

  useEffect(() => {
    writeUserPrefs(prefs);
  }, [prefs]);

  const update = useCallback(
    (next: Partial<SalesCrmUserPreferences>) => {
      setPrefs((prev) => ({ ...prev, ...next }));
    },
    [],
  );

  return [prefs, update];
}
